import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Vocab } from './useVocab'
import { useSearchParams } from 'react-router-dom'
import { LanguageCode } from 'src/models/language'
import {
  useMutation,
  useQuery,
} from '@tanstack/react-query'
import { get, put } from 'src/utils/http'
import useAuth from './useAuth'

export type Session = {
  fromLang: LanguageCode
  toLang: LanguageCode
  characterNum: number
  documentId: string
  vocab?: Vocab[] | any[]
}

// const LOCAL_STORAGE_SESSION_KEY = 'lahilo-session'

export const useLatestSession = ({
  fromLang,
  toLang,
}:
  | { fromLang?: string; toLang?: string }
  | undefined = {}) => {
  useAuth()
  const { data: latestSession, isLoading } =
    useQuery<Session>(
      ['session', 'latest'],
      () =>
        get(`users/sessions/latest`, {
          params: {
            fromLang,
            toLang,
          },
        }),
      // {
      //   enabled: !isAuthLoading && isLoggedIn,
      // },
    )

  return {
    latestSession,
    isLoading,
  }
}

const useSession = () => {
  const { isLoggedIn } = useAuth()
  const isDemo = !isLoggedIn

  const [searchParams] = useSearchParams()

  const searchParamSession: Partial<Session> =
    useMemo(() => {
      const routeFromLang = searchParams.get('fromLang')
      const routeToLang = searchParams.get('toLang')
      const docId = searchParams.get('documentId')

      return {
        ...(routeFromLang
          ? { fromLang: routeFromLang as LanguageCode }
          : {}),
        ...(routeToLang
          ? { toLang: routeToLang as LanguageCode }
          : {}),
        ...(docId ? { documentId: docId } : {}),
      }
    }, [searchParams])

  const fromLang = useMemo(
    () => searchParamSession?.fromLang,
    [searchParamSession?.fromLang],
  )
  const toLang = useMemo(
    () => searchParamSession?.toLang,
    [searchParamSession?.toLang],
  )

  const {
    data: savedDocumentSession,
    isFetching: isLoading,
  } = useQuery<Session>(
    [
      'session',
      searchParamSession?.documentId,
      fromLang,
      toLang,
    ],
    () =>
      get(
        `users/sessions/documents/${searchParamSession?.documentId}`,
        {
          params: {
            fromLang: fromLang,
            toLang: toLang,
          },
        },
      ).then((r) => {
        if (r) return r
        return {
          documentId: searchParamSession?.documentId,
          fromLang,
          toLang,
          characterNum: 1,
        }
      }),
    {
      enabled:
        !isDemo &&
        !!searchParamSession?.documentId &&
        !!fromLang &&
        !!toLang,
    },
  )
  const [session, setSession] = useState<
    Session | undefined
  >()

  useEffect(() => {
    if (isLoading) return
    setSession(savedDocumentSession)
  }, [isLoading, setSession, savedDocumentSession])

  const { mutate: updateSession } = useMutation(
    [],
    (s: Session) =>
      put(
        `users/sessions/documents/${s?.documentId}`,
        {
          ...s,
          vocab: (s?.vocab ?? [])
            .filter(
              (x: Vocab | []) => (x as [])?.length !== 0,
            )
            .map(
              ({ word, characterNum, numEncounters }) => [
                word.token,
                word.clean,
                characterNum,
                numEncounters || 1,
              ],
            ),
        },
        {
          params: {
            fromLang: s?.fromLang,
            toLang: s?.toLang,
          },
        },
      )
      .then((response) => response.json())
      .then((body) => setSession(body))
  )

  const save = useCallback(
    (s: Partial<Session>) => {
      if (isLoading || isDemo) return

      setSession((currSession: Session | undefined) => {
        const newSession = {
          ...(currSession ?? {}),
          ...(s ?? {}),
        } as unknown as Session

        updateSession(newSession)

        return newSession
      })
    },
    [isDemo, isLoading, updateSession],
  )

  return {
    session,
    searchParamSession,
    save,
  }
}

export default useSession
